const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const CONSUMER_BASE = `${API_BASE}/api/public/consumer`;
const ORIGIN =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost';

async function handleResponse(response) {
  if (!response.ok) {
    const clone = response.clone();
    let message = 'Request failed';
    try {
      const body = await clone.json();
      message = body?.error || JSON.stringify(body);
    } catch (error) {
      try {
        message = await clone.text();
      } catch (textError) {
        // ignore secondary failure, retain default message
      }
    }
    throw new Error(message);
  }
  return response.json();
}

export async function fetchPackaging({ query, signal } = {}) {
  const url = new URL(`${CONSUMER_BASE}/packaging`, ORIGIN);
  if (query) {
    url.searchParams.set('q', query);
  }
  const response = await fetch(url.toString(), { signal });
  const payload = await handleResponse(response);
  return payload.data || [];
}

export async function submitVerification(payload) {
  const response = await fetch(`${CONSUMER_BASE}/verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data.data;
}

export async function fetchVerification(sessionId) {
  const response = await fetch(`${CONSUMER_BASE}/verification/${sessionId}`);
  const payload = await handleResponse(response);
  return payload.data;
}

export async function searchBins({ zip, radiusMi = 25, limit = 10 }) {
  const url = new URL(`${CONSUMER_BASE}/bin-locations`, ORIGIN);
  url.searchParams.set('zip', zip);
  url.searchParams.set('radiusMi', radiusMi.toString());
  url.searchParams.set('limit', limit.toString());
  const response = await fetch(url.toString());
  const payload = await handleResponse(response);
  return payload.data;
}

export async function requestMailBack(payload) {
  const response = await fetch(`${CONSUMER_BASE}/mail-back`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data.data;
}

export async function fetchMailBackStatus(trackingNumber) {
  if (!trackingNumber) {
    throw new Error('Tracking number required');
  }
  const response = await fetch(`${CONSUMER_BASE}/mail-back/${encodeURIComponent(trackingNumber)}`);
  const payload = await handleResponse(response);
  return payload.data;
}

export async function consumerSignup(payload) {
  const response = await fetch(`${CONSUMER_BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data;
}

export async function consumerLogin(payload) {
  const response = await fetch(`${CONSUMER_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data;
}

export async function consumerProfile(token) {
  const response = await fetch(`${CONSUMER_BASE}/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await handleResponse(response);
  return data.user;
}

export async function updateConsumerProfile(token, payload) {
  const response = await fetch(`${CONSUMER_BASE}/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse(response);
  return data.user;
}

export async function recordEvent(payload) {
  try {
    await fetch(`${CONSUMER_BASE}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Non-blocking
    console.warn('Failed to record consumer event', error);
  }
}
